import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router';
import { Button } from '@/kit/Button';
import { NoteTagsTab } from '@/features/settings-b/NoteTagsTab';
import { InputField, TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { SectionTabs } from '@/kit/SectionTabs';
import { Select, SelectOption } from '@/kit/Select';
import { Toggle } from '@/kit/Toggle';
import { openDialog } from '@/kit/dialog';
import { useAutosave } from '@/kit/hooks/useAutosave';
import { appendCapiumTag, capiumTagGroupsFor } from '@/core/models/capium-tags';
import type { FirmAccountantRow } from '@/core/models/firm-settings.models';
import {
  ReportNoteSection,
  type AccountantReportSettings,
  type CompanyReportAccountant,
  type CompanyReportPickItem,
  type ReportNoteTextRow,
} from '@/core/models/report-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { reportSettingsService } from '@/core/services/reportSettings';
import { accountingBodyItems, isKnownAccountingBody } from '@/features/settings/accountantForm';
import { AddAccountantDialog } from './AddAccountantDialog';
import { useAccountantReportTitle, useClientAudited } from './auditStatus';
import { isUnincorporated, useClientKind } from './clientKind';
import { SETTINGS_ROUTE, useReportClient } from './common';
import { StickyReportSettingsFrame } from './StickyReportSettingsFrame';
import { standardNoteEditorConfig } from './noteEditorConfig';
import { RelatedNoteDialog, type RelatedNoteDialogData } from './RelatedNoteDialog';
import './AccountantReport.scss';

const editorConfig = standardNoteEditorConfig();

type FlagKey =
  | 'showOnCompanyInfo'
  | 'removeDottedSignatureLine'
  | 'replaceInCompanyInfo'
  | 'replaceInReportPage'
  | 'isRegisteredAuditor';

/** BUG-031: 3.0's last accounting body; with it the screen asks for the body's name. "Other" is its old name. */
const OTHER_ACCOUNTING_BODY = 'Other/None';
const isOtherAccountingBody = (body: string | null | undefined) =>
  !!body && [OTHER_ACCOUNTING_BODY.toLowerCase(), 'other'].includes(body.trim().toLowerCase());

/**
 * Report Settings > Accountant's Report. The details (700 ms, one snapshot compare) and the notes (700 ms, a per-note
 * diff) are two independent autosave streams, both flushed when the page is left. * BUG-031: as in 3.0, "Add new accountant" adds a firm accountant and selects it, and "Save Accountant" saves the
 * details straight away and says whether that worked. Single-page layout unifies Accountant's Contact and
 * Related Notes seamlessly into one clean page.
 */
export function AccountantReport() {
  const navigate = useNavigate();
  const client = useReportClient();
  const kind = useClientKind(client?.id);
  /** #200: "Auditor's Report" when the accounts are audited (Report Settings > Audit Status). */
  const title = useAccountantReportTitle(client?.id);
  const audited = useClientAudited(client?.id);
  /** BUG-075: the same details, named for the auditor of audited accounts. */
  const person = audited ? 'Auditor' : 'Accountant';
  const isSoleTrader =
    isUnincorporated(kind?.clientType) || Boolean(client?.name?.toLowerCase().includes('soletrader'));
  // The Capium Tags panel follows the client's type (APT312; an LLP reads "member(s)" - BUG-072 round 2).
  const tagGroups = capiumTagGroupsFor(kind?.clientType);
  const accountantPlaceholder = audited
    ? 'Select auditor...'
    : isSoleTrader
      ? '(Select Accountant)'
      : 'Select accountant...';
  const accountantNameLabel = isSoleTrader ? 'Firm Name' : `${person} Name`;
  const officeLocationLabel = isSoleTrader ? 'Office/Location' : 'Office Location';

  const [details, setDetailsState] = useState<AccountantReportSettings | null>(null);
  const [auditors, setAuditors] = useState<CompanyReportPickItem[]>([]);
  /** ap5-a5: the firm's accountants (Settings > Accountants); the picker used to list firm user names. */
  const [accountants, setAccountants] = useState<CompanyReportAccountant[]>([]);
  const [accountingBodies, setAccountingBodies] = useState<string[]>([]);
  const accountingBodiesRef = useRef<string[]>([]);
  /** Every Section 2 (Accountant's Report) note the catalogue defines - dynamic. */
  const [notes, setNotesState] = useState<ReportNoteTextRow[]>([]);

  const detailsRef = useRef<AccountantReportSettings | null>(null);
  const notesRef = useRef<ReportNoteTextRow[]>([]);
  const savedDetails = useRef('');
  const [isSavingAccountant, setIsSavingAccountant] = useState(false);
  const savedNotes = useRef(new Map<string, { isEnabled: boolean; content: string }>());
  // The picker's lists are needed inside onAuditorChange, which reads the latest render's values.
  const accountantsRef = useRef<CompanyReportAccountant[]>([]);
  const auditorsRef = useRef<CompanyReportPickItem[]>([]);

  const setDetails = (next: AccountantReportSettings | null) => {
    detailsRef.current = next;
    setDetailsState(next);
  };
  const setNotes = (next: ReportNoteTextRow[]) => {
    notesRef.current = next;
    setNotesState(next);
  };

  const sendDetails = async (snapshot: string) => {
    await reportSettingsService.saveAccountantReport(JSON.parse(snapshot));
    savedDetails.current = snapshot;
  };

  const persistDetails = async () => {
    const current = detailsRef.current;
    if (!current) return;
    const snapshot = JSON.stringify(current);
    if (snapshot === savedDetails.current) return;
    try {
      await sendDetails(snapshot);
    } catch (error) {
      notifyError(error);
    }
  };

  const persistNotes = async () => {
    if (!client) return;
    const changed = notesRef.current
      .map((note) => ({ note, saved: savedNotes.current.get(note.noteKey) }))
      .filter(({ note, saved }) => !saved || saved.isEnabled !== note.isEnabled || saved.content !== note.content)
      .map(({ note, saved }) => ({
        noteKey: note.noteKey,
        isEnabled: note.isEnabled,
        setText: !saved || saved.content !== note.content,
        title: note.title || note.name || "Accountant's Report",
        content: note.content,
        snapshot: { isEnabled: note.isEnabled, content: note.content },
      }));
    if (changed.length === 0) return;
    try {
      await reportSettingsService.saveReportNoteTexts(
        client.id,
        ReportNoteSection.AccountantReport,
        changed.map(({ snapshot, ...input }) => {
          void snapshot;
          return input;
        }),
      );
      changed.forEach((c) => savedNotes.current.set(c.noteKey, c.snapshot));
    } catch (error) {
      notifyError(error);
    }
  };

  const detailsSave = useAutosave({ delayMs: 700, mode: 'switch', flushOnUnmount: 'always', persist: persistDetails });
  const notesSave = useAutosave({ delayMs: 700, mode: 'switch', flushOnUnmount: 'always', persist: persistNotes });

  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    Promise.all([reportSettingsService.getOptions(), reportSettingsService.getAccountantReport(client.id)]).then(
      ([options, view]) => {
        if (cancelled) return;
        auditorsRef.current = options.auditors;
        accountantsRef.current = options.accountants ?? [];
        setAuditors(options.auditors);
        setAccountants(options.accountants ?? []);
        accountingBodiesRef.current = options.accountingBodies;
        setAccountingBodies(options.accountingBodies);
        setDetails(view.details);
        savedDetails.current = JSON.stringify(view.details);
        const mappedNotes = (view.notes ?? []).map((n) => {
          const fallbackTitle = n.title || n.name || "Accountant's Report";
          if (!audited && (n.noteKey === 'opinion' || n.name === 'Opinion' || n.title === 'Opinion')) {
            return { ...n, name: "Accountant's Report", title: "Accountant's Report" };
          }
          return { ...n, title: fallbackTitle };
        });
        setNotes(mappedNotes);
        savedNotes.current.clear();
        mappedNotes.forEach((n) => savedNotes.current.set(n.noteKey, { isEnabled: n.isEnabled, content: n.content }));
      },
      (error) => {
        if (!cancelled) notifyError(error);
      },
    );
    return () => {
      cancelled = true;
    };
    // Loads once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDetailsChange = () => detailsSave.queueSave();

  const changeDetails = (patch: Partial<AccountantReportSettings>) => {
    if (!detailsRef.current) return;
    setDetails({ ...detailsRef.current, ...patch });
    onDetailsChange();
  };

  /**
   * ap5-a5: choosing an accountant fills in the name, address, postcode and accounting body held against them in
   * Settings > Accountants.
   */
  const onAuditorChange = (accountantId: string | null) => {
    const current = detailsRef.current;
    if (!current) return;
    const next: AccountantReportSettings = { ...current, auditorUserId: accountantId ?? null };
    const accountant = accountantsRef.current.find((a) => a.id === accountantId);
    if (accountant) {
      next.firstName = accountant.name;
      if (accountant.address) next.address = accountant.address;
      if (accountant.postCode) next.postCode = accountant.postCode;
      // BUG-015: a body the list no longer offers (CAI, CIOT, ATT) is still copied - the API keeps accepting it.
      if (accountant.accountingBody && isKnownAccountingBody(accountingBodiesRef.current, accountant.accountingBody)) {
        next.accountingBody = accountant.accountingBody;
        if (isOtherAccountingBody(accountant.accountingBody))
          next.accountingBodyName = accountant.accountingBodyName ?? null;
        else if (current.accountingBodyName) next.accountingBodyName = null;
      }
    } else {
      // A client saved before the change still holds a firm user id.
      const auditor = auditorsRef.current.find((a) => a.id === accountantId);
      if (auditor) next.firstName = auditor.name;
    }
    setDetails(next);
    onDetailsChange();
  };

  /**
   * BUG-031 "Save Accountant": saves the details now (dropping the pending autosave, which would send the same
   * snapshot) and reports the outcome - success only once the server has accepted them.
   */
  const onSaveAccountant = async () => {
    const current = detailsRef.current;
    if (isSavingAccountant) return;
    detailsSave.cancel();
    notesSave.cancel();
    setIsSavingAccountant(true);
    try {
      await Promise.all([current ? sendDetails(JSON.stringify(current)) : Promise.resolve(), persistNotes()]);
      notifySuccess('Accountant saved successfully');
    } catch (error) {
      notifyError(error);
    } finally {
      setIsSavingAccountant(false);
    }
  };

  /**
   * BUG-031 "Add new accountant": the dialog saves a firm accountant (Settings > Accountants); the picker is then
   * re-read from the server so the new accountant is listed with the address the server builds, and is selected.
   */
  const onAddAccountant = () => {
    openDialog<null, FirmAccountantRow>(AddAccountantDialog, { disableClose: true, panelClass: 'cst-invite' })
      .afterClosed()
      .then((row) => {
        if (!row) return;
        reportSettingsService.getOptions().then(
          (options) => {
            accountantsRef.current = options.accountants ?? [];
            setAccountants(options.accountants ?? []);
            onAuditorChange(row.id);
          },
          (error) => notifyError(error),
        );
      });
  };

  const setFlag = (key: FlagKey, value: boolean) => changeDetails({ [key]: value });

  const changeNote = (noteKey: string, patch: Partial<ReportNoteTextRow>) => {
    setNotes(notesRef.current.map((note) => (note.noteKey === noteKey ? { ...note, ...patch } : note)));
    notesSave.queueSave();
  };

  const setNoteEnabled = (note: ReportNoteTextRow, value: boolean) => changeNote(note.noteKey, { isEnabled: value });
  const setNoteContent = (note: ReportNoteTextRow, value: string) => changeNote(note.noteKey, { content: value ?? '' });
  const insertTag = (note: ReportNoteTextRow, tag: string) => {
    const latest = notesRef.current.find((n) => n.noteKey === note.noteKey) ?? note;
    setNoteContent(note, appendCapiumTag(latest.content, tag));
  };

  /** APT256: "Edit" opens the note's wording in RelatedNoteDialog's larger view; Save writes it back like any other edit. */
  const openNoteDialog = (note: ReportNoteTextRow) => {
    const latest = notesRef.current.find((n) => n.noteKey === note.noteKey) ?? note;
    openDialog<RelatedNoteDialogData, string>(RelatedNoteDialog, {
      disableClose: true,
      data: { heading: note.name, content: latest.content, defaultContent: note.defaultContent, tagGroups },
    })
      .afterClosed()
      .then((result) => {
        if (result == null) return;
        setNoteContent(note, result);
      });
  };

  const flag = (key: FlagKey, label: string) => (
    <Toggle checked={Boolean(details?.[key])} onCheckedChange={(checked) => setFlag(key, checked)}>
      <p className="m-0 d-inline">{label}</p>
    </Toggle>
  );

  return (
    <StickyReportSettingsFrame
      title={title}
      hostClassName="accountant-report-host"
      onClose={() => navigate(SETTINGS_ROUTE)}
      // 3 Nov: one Save in the sticky header on both tabs (it was at the foot of each tab). The page also autosaves; this saves now.
      actions={
        details && (
          <Button
            className="action-button primary-button btn-save-accountant"
            busy={isSavingAccountant}
            onClick={() => void onSaveAccountant()}
            title="Save Accountant"
            aria-label="Save Accountant"
            data-testid="save-accountant-btn"
          >
            <Icon name="save" outlined />
            <span>Save</span>
          </Button>
        )
      }
    >
      {details && (
        <div className="accountant-report-single-page">
          <div className="ar-main-card">
            {/* Design review 2 Nov 2026: the contact details and the related notes are two tabs, not one long page. */}
            <SectionTabs
              ariaLabel="Accountant’s Report sections"
              tabs={[
                { id: 'contact', label: 'Accountant’s Contact' },
                { id: 'notes', label: 'Related Notes' },
              ]}
            >
              {(active) =>
                active === 'contact' ? (
                  /* Section 1: Accountant's Contact */
                  <div className="ar-section ar-contact-section">
                    <div className="ar-section-header">
                      <h3 className="ar-section-title">Accountant's Contact</h3>
                    </div>

                    <div className="ar-contact-layout">
                      {/* Left Column: Accountant Details */}
                      <div className="ar-contact-col">
                        {/* 1. Accountant dropdown + Add button */}
                        <div className="form-field ar-form-row">
                          {/* task-16: the accountants list (from firm Settings) takes priority; firm users are a fallback so
                        clients saved with a user id still resolve. */}
                          <p>{person}</p>
                          <div className="ar-field-control">
                            <div className="d-flex align-items-center gap-10">
                              <Select
                                className="w-select-acc"
                                value={details.auditorUserId}
                                onChange={(value) => onAuditorChange(value as string | null)}
                                placeholder={accountantPlaceholder}
                              >
                                {accountants.length > 0 &&
                                  accountants.map((accountant) => (
                                    <SelectOption key={accountant.id} value={accountant.id}>
                                      {accountant.name}
                                    </SelectOption>
                                  ))}
                                {accountants.length === 0 &&
                                  auditors.length > 0 &&
                                  auditors.map((auditor) => (
                                    <SelectOption key={auditor.id} value={auditor.id}>
                                      {auditor.name}
                                    </SelectOption>
                                  ))}
                                {accountants.length === 0 && auditors.length === 0 && (
                                  <SelectOption disabled value={null}>
                                    No accountants found — add one in General Settings → Accountants
                                  </SelectOption>
                                )}
                              </Select>
                              <Button
                                className="action-button primary-button btn-add-accountant"
                                onClick={onAddAccountant}
                                title="Add new accountant"
                                aria-label="Add new accountant"
                              >
                                <Icon name="add" />
                                <span>Add new accountant</span>
                              </Button>
                            </div>
                            {accountants.length === 0 && (
                              <p className="font-size-12 text-gray mt-5">
                                <Icon name="info" outlined style={{ fontSize: '13px', verticalAlign: 'middle' }} /> For
                                full details (address, accounting body), add accountants in{' '}
                                <Link to="/home/settings/accountants" className="link">
                                  General Settings → Accountants
                                </Link>
                                .
                              </p>
                            )}
                          </div>
                        </div>

                        {/* 2. QA rows 206/207 (BUG-014): Firm Name or Accountant Name */}
                        <div className="form-field ar-form-row">
                          <p>{accountantNameLabel}</p>
                          <div className="ar-field-control">
                            <InputField
                              fieldClassName="w-input-standard"
                              maxLength={255}
                              value={details.firstName ?? ''}
                              onChange={(event) => changeDetails({ firstName: event.target.value })}
                              charMode="name"
                            />
                            {!details.firstName?.trim() && !details.firmName?.trim() && (
                              <p className="font-size-12 text-danger mb-1 mt-5">
                                No name will be signed on the {person}’s report. Enter an {person} Name, or a firm Name
                                below.
                              </p>
                            )}
                          </div>
                        </div>

                        {/* 3. Address */}
                        <div className="form-field ar-form-row textarea">
                          <p>Address</p>
                          <div className="ar-field-control">
                            <TextareaField
                              fieldClassName="w-input-standard"
                              maxLength={500}
                              value={details.address ?? ''}
                              onChange={(event) => changeDetails({ address: event.target.value })}
                            />
                          </div>
                        </div>

                        {/* 4. Postcode */}
                        <div className="form-field ar-form-row">
                          <p>Postcode</p>
                          <div className="ar-field-control">
                            <InputField
                              fieldClassName="w-input-postcode"
                              maxLength={255}
                              value={details.postCode ?? ''}
                              onChange={(event) => changeDetails({ postCode: event.target.value })}
                              charMode="alphanumeric"
                            />
                          </div>
                        </div>

                        {/* 5. Accounting Body and optional inline Accounting body name */}
                        <div className="form-field ar-form-row">
                          <p>{'Accounting  Body'}</p>
                          <div className="ar-field-control d-flex align-items-center gap-10 flex-wrap">
                            <Select
                              className={
                                isOtherAccountingBody(details.accountingBody) ? 'w-select-body' : 'w-input-standard'
                              }
                              items={accountingBodyItems(accountingBodies, details.accountingBody)}
                              value={details.accountingBody}
                              onChange={(value) => changeDetails({ accountingBody: value as string | null })}
                            />
                            {/* BUG-031: as in 3.0, Other/None asks for the accounting body's name on the same row */}
                            {isOtherAccountingBody(details.accountingBody) && (
                              <div className="form-field m-0 d-inline-flex">
                                <p className="cdk-visually-hidden">Accounting body name</p>
                                <InputField
                                  fieldClassName="w-input-body-name"
                                  placeholder="Accounting body name"
                                  maxLength={255}
                                  value={details.accountingBodyName ?? ''}
                                  onChange={(event) => changeDetails({ accountingBodyName: event.target.value })}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Checkboxes aligned under input column */}
                        <div className="ar-form-row">
                          <div className="ar-label-spacer" />
                          <div className="ar-field-control d-flex flex-column gap-6">
                            {flag('showOnCompanyInfo', 'Show on company information page')}
                            {audited
                              ? flag('isRegisteredAuditor', 'Registered Auditor')
                              : flag('removeDottedSignatureLine', 'Remove dotted signature line in Accountants report')}
                          </div>
                        </div>
                      </div>

                      {/* Right Column: Presentation of Firm */}
                      <div className="ar-presentation-col">
                        <div className="ar-presentation-cardlet">
                          <div className="form-field-title ar-subheading">
                            <p>Presentation of Firm (Replace Accountant/Auditor Name)</p>
                          </div>

                          {/* 6. Name */}
                          <div className="form-field ar-form-row">
                            <p>Name</p>
                            <div className="ar-field-control">
                              <InputField
                                fieldClassName="w-input-standard"
                                maxLength={255}
                                value={details.firmName ?? ''}
                                onChange={(event) => changeDetails({ firmName: event.target.value })}
                              />
                            </div>
                          </div>

                          {/* 7. Office Location */}
                          <div className="form-field ar-form-row">
                            <p>{officeLocationLabel}</p>
                            <div className="ar-field-control">
                              <InputField
                                fieldClassName="w-input-standard"
                                maxLength={255}
                                value={details.officeLocation ?? ''}
                                onChange={(event) => changeDetails({ officeLocation: event.target.value })}
                              />
                            </div>
                          </div>

                          {/* Presentation Checkboxes aligned under input column */}
                          <div className="ar-form-row">
                            <div className="ar-label-spacer" />
                            <div className="ar-field-control d-flex flex-column gap-6">
                              {flag('replaceInCompanyInfo', 'Replace in Company Information Page')}
                              {flag('replaceInReportPage', 'Replace in Accountant/Auditor Report Page')}
                            </div>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Section 2: Related Notes */
                  <div className="ar-section ar-notes-section">
                    <div className="ar-section-header">
                      <div className="form-field-title m-0">
                        <p>Related Notes</p>
                      </div>
                    </div>

                    <div className="ar-notes-body">
                      {notes.map((note, index) => (
                        <div key={note.noteKey} className={`ar-note-block ${index > 0 ? 'mt-1 hr-top pt-1' : ''}`}>
                          <div className="note-checkbox-row mb-10 d-flex align-items-center justify-space-between">
                            <Toggle
                              checked={note.isEnabled}
                              onCheckedChange={(checked) => setNoteEnabled(note, checked)}
                            >
                              {note.name}
                            </Toggle>
                            <Button
                              className="btn-expand-note"
                              onClick={() => openNoteDialog(note)}
                              title="Edit in a larger view"
                              aria-label={`Edit ${note.name}`}
                            >
                              <Icon name="edit" outlined />
                            </Button>
                          </div>
                          <div className="note-editor-wrapper">
                            <div className="editor-col">
                              <div className="form-field textarea">
                                <RichTextEditor
                                  id={`${note.noteKey}Editor`}
                                  value={note.content}
                                  onChange={(html) => setNoteContent(note, html)}
                                  config={editorConfig}
                                />
                                {/* AP parity: the 3.0 note popup's "Reset Default" puts the catalogue wording back */}
                                {note.defaultContent != null && (
                                  <div className="d-flex justify-content-end pt-5">
                                    <a
                                      className="link hyperlink"
                                      onClick={() => setNoteContent(note, note.defaultContent ?? '')}
                                    >
                                      Reset Default
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                            <NoteTagsTab groups={tagGroups} onInsert={(tag) => insertTag(note, tag)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              }
            </SectionTabs>
          </div>
        </div>
      )}
    </StickyReportSettingsFrame>
  );
}
